import { money } from "@/lib/api";

/** Operator view shows every line. Passengers see the price they pay, nothing else — `simple`. */
export const QuoteBreakdown = ({ quote, miles, testId = "quote-breakdown",
                                 label = "Price breakdown", simple = false }) => {
  if (!quote) return null;
  if (simple) {
    const dueNow = Number(quote.deposit_due || 0);
    const after = Number(quote.balance_due || 0);
    return (
      <div data-testid={testId} className="rounded-lg border border-gold/30 bg-gold/5 p-5">
        <p className="font-mono text-[11px] uppercase tracking-widest text-gold">{label}</p>
        <p data-testid={`${testId}-total`} className="mt-2 font-display text-3xl font-bold text-slate-50">
          {money(quote.total)}
        </p>
        <p data-testid={`${testId}-note`} className="mt-1.5 text-xs text-slate-400">
          {dueNow > 0 && after > 0
            ? `${money(dueNow)} due now to confirm, ${money(after)} after your ride.`
            : dueNow > 0
              ? "Payable now to confirm your reservation."
              : "Nothing to pay now — settle after your ride."}
        </p>
        {quote.miles_note && (
          <p data-testid={`${testId}-estimate-note`} className="mt-2 text-[11px] leading-relaxed text-amber-300">
            {quote.miles_note}
          </p>
        )}
      </div>
    );
  }
  return (
    <div data-testid={testId} className="rounded-lg border border-ink-600 bg-ink-900/60 p-4">
      <div className="flex items-center justify-between">
        <p className="font-mono text-[11px] uppercase tracking-widest text-gold">
          {label} · {quote.flat_rate ? "flat rate" : quote.pricing_mode === "hourly" ? "hourly" : "mileage"}
        </p>
        {!quote.flat_rate && (miles ?? quote.miles) != null && (
          <span data-testid={`${testId}-miles`} className="font-mono text-[11px] text-slate-500">
            {Number(miles ?? quote.miles).toFixed(1)} mi{quote.miles_source === "estimate" ? " (est.)" : ""}
          </span>
        )}
      </div>
      {quote.miles_note && (
        <p data-testid={`${testId}-estimate-note`} className="mt-2 text-[11px] leading-relaxed text-amber-300">
          {quote.miles_note}
        </p>
      )}
      <div className="mt-3 space-y-1.5">
        {(quote.lines || []).map((l, i) => (
          <div key={`${l.label}-${i}`} className="flex justify-between gap-4 text-sm">
            <span className="text-slate-400">{l.label}</span>
            <span className="font-mono text-slate-200">{money(l.amount)}</span>
          </div>
        ))}
      </div>
      <div className="mt-3 flex justify-between border-t border-ink-600 pt-3">
        <span className="text-sm text-slate-300">Total</span>
        <span data-testid={`${testId}-total`} className="font-display text-lg text-slate-50">{money(quote.total)}</span>
      </div>
      <div className="mt-3 flex flex-wrap gap-6">
        <div>
          <p className="text-xs text-slate-500">At reservation</p>
          <p data-testid={`${testId}-deposit`} className="font-display text-base text-slate-100">{money(quote.deposit_due)}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500">After trip</p>
          <p data-testid={`${testId}-balance`} className="font-display text-base text-slate-100">{money(quote.balance_due)}</p>
        </div>
      </div>
    </div>
  );
};
